import { supabase } from "@/libs/supabase";
import type { Event, DbEvent } from "@/types/event";

// DbEventからEventへの変換関数
const dbEventToEvent = (dbEvent: DbEvent): Event => ({
	id: dbEvent.id,
	title: dbEvent.title,
	description: dbEvent.description || undefined,
	startDateTime: dbEvent.start_date_time,
	endDateTime: dbEvent.end_date_time,
	location: dbEvent.location || undefined,
	isOnline: dbEvent.is_online,
	participationUrl: dbEvent.participation_url || undefined,
	createdBy: dbEvent.created_by,
	createdAt: dbEvent.created_at,
	updatedAt: dbEvent.updated_at,
	participantCount: 0, // 別途取得する必要があります
});

// イベント一覧を取得
export const fetchEvents = async (): Promise<Event[]> => {
	const { data, error } = await supabase.from("events").select(`
      *,
      event_participants(count)
    `);

	if (error) {
		console.error("イベント取得エラー:", error);
		throw error;
	}

	return data.map((event) => ({
		...dbEventToEvent(event),
		participantCount: event.event_participants[0]?.count || 0,
	}));
};

// イベントを作成
export const createEvent = async (
	event: Omit<Event, "id" | "createdAt" | "updatedAt">,
): Promise<Event> => {
	const { data, error } = await supabase
		.from("events")
		.insert([
			{
				title: event.title,
				description: event.description,
				start_date_time: event.startDateTime,
				end_date_time: event.endDateTime,
				location: event.location,
				is_online: event.isOnline,
				participation_url: event.participationUrl,
				created_by: event.createdBy,
			},
		])
		.select()
		.single();

	if (error) {
		console.error("イベント作成エラー:", error);
		throw error;
	}

	return dbEventToEvent(data);
};

// イベントを更新
export const updateEvent = async (
	id: string,
	event: Partial<Event>,
): Promise<Event> => {
	const { data, error } = await supabase
		.from("events")
		.update({
			title: event.title,
			description: event.description,
			start_date_time: event.startDateTime,
			end_date_time: event.endDateTime,
			location: event.location,
			is_online: event.isOnline,
			participation_url: event.participationUrl,
			updated_at: new Date().toISOString(),
		})
		.eq("id", id)
		.select()
		.single();

	if (error) {
		console.error("イベント更新エラー:", error);
		throw error;
	}

	return dbEventToEvent(data);
};

// イベントを削除
export const deleteEvent = async (id: string): Promise<void> => {
	const { error } = await supabase.from("events").delete().eq("id", id);

	if (error) {
		console.error("イベント削除エラー:", error);
		throw error;
	}
};
